/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import {
  Array,
  BigNumber as BigNumber_,
  Iterable,
  Map,
  Order,
  pipe,
  Record,
} from '@blade/std-lib'
import { CurrencyAddress, CurrencyModel } from '@root/entities/currency'
import { Network } from '@root/entities/network'
import { pairRegistry, type PairModel } from '@root/entities/pair/atoms'
import { PairAddress } from '@root/entities/pair/model'
import { environmentAtom } from '@root/shared/environment'

import { WalletAddress, WalletId } from '../model'

import { reatomWalletAsset } from './reatom-wallet-asset'

import type { Ctx, CtxSpy } from '@reatom/framework'
import type { WalletApi } from '@root/shared/api'
import type { TokenLogosHost } from '@root/shared/models'

export const reatomWallet = (
  dto: WalletApi.WalletDto,
  imagesHost: TokenLogosHost,
  assetsPairs: Map<PairAddress, PairModel>,
) => {
  const network = Network.fromDto(dto.chain)
  //#region State

  const orderAtom = atom(dto.order ?? 0)
  const colorAtom = atom(dto.color)
  const nameAtom = atom(dto.name)
  const favoriteAtom = atom(dto.favorite)
  /*
   * Whose key it is. A wallet we hold the key for can be exported and signed
   * for here; one whose key is on somebody's machine can only be signed for
   * there. Without this the screen offers both and finds out by failing.
   */
  const custodyAtom = atom(dto.custody ?? 'server')

  const assetsByCurrencyAtom = atom(
    pipe(
      dto.assets.map((it) =>
        reatomWalletAsset(it, network, imagesHost, assetsPairs),
      ),
      Iterable.toRecord(
        (it) => it.currencyAddress,
        (it) => it,
      ),
    ),
  )
  //#endregion

  const assetsAtom = atom((ctx) => {
    const assets = Object.values(ctx.spy(assetsByCurrencyAtom))

    const cmp = reatomWalletAsset.cmpDefault(ctx)

    return pipe(assets, Array.sort(cmp))
  })

  const totalBalanceNative = atom((ctx) => {
    const assets = ctx.spy(assetsAtom)
    return pipe(
      assets,
      Iterable.reduce(BigNumber(0), (acc, asset) => {
        const balanceNative = ctx.spy(asset.balanceNative)
        return acc.plus(balanceNative)
      }),
    )
  })

  const totalBalanceUsd = atom((ctx) => {
    const assets = ctx.spy(assetsAtom)
    return pipe(
      assets,
      Iterable.reduce(BigNumber(0), (acc, asset) => {
        const balanceUsd = ctx.spy(asset.balanceUsd)
        return acc.plus(balanceUsd)
      }),
    )
  })

  const nativeAssetAtom = atom((ctx) => {
    return ctx.spy(assetsByCurrencyAtom)[
      CurrencyModel.nativeOf(network).address
    ]
  })

  const nativeAssetBalanceAtom = atom((ctx) => {
    const nativeAssetModel = ctx.spy(nativeAssetAtom)
    if (!nativeAssetModel) {
      return BigNumber(0)
    }
    return ctx.spy(nativeAssetModel.balance)
  })
  const nativeAssetBalanceUsdAtom = atom((ctx) => {
    const nativeAssetModel = ctx.spy(nativeAssetAtom)
    if (!nativeAssetModel) {
      return BigNumber(0)
    }
    return ctx.spy(nativeAssetModel.balanceUsd)
  })

  const updateFromDtoAction = action((ctx, dto: WalletApi.WalletDto) => {
    nameAtom(ctx, dto.name)
    colorAtom(ctx, dto.color)
    favoriteAtom(ctx, dto.favorite)
    custodyAtom(ctx, dto.custody ?? 'server')
    orderAtom(ctx, dto.order ?? 0)
    const assets = ctx.get(assetsByCurrencyAtom)

    for (const assetDto of dto.assets) {
      const assetModel = assets[CurrencyAddress(assetDto.address)]
      if (assetModel) {
        assetModel.updateFromDto(ctx, assetDto)
        continue
      }
      const assetsPairs = Map.create<PairAddress, PairModel>([])
      if (assetDto.pairMeta) {
        assetsPairs.set(
          PairAddress(assetDto.pairMeta.poolAddress),
          pairRegistry.resolveFromDto(ctx, assetDto.pairMeta),
        )
      }
      const newAssetModel = reatomWalletAsset(
        assetDto,
        network,
        imagesHost,
        assetsPairs,
      )
      assetsByCurrencyAtom(ctx, (it) =>
        pipe(it, Record.set(newAssetModel.currencyAddress, newAssetModel)),
      )
    }
  })

  const visibleAssetsAtom = atom((ctx) => {
    const assets = ctx.spy(assetsAtom)
    return assets.filter((asset) => !ctx.spy(asset.hidden))
  })

  const searchAssetsAction = action((ctx, query: string) => {
    const assets = ctx.get(visibleAssetsAtom)
    return assets.filter(
      (it) =>
        it.currency.title.toLowerCase().includes(query.toLowerCase()) ||
        it.currency.ticker.toLowerCase().includes(query.toLowerCase()),
    )
  })

  const asset = (currencyAddress: CurrencyAddress) => {
    const theAtom = atom((ctx) => {
      const asset = ctx.spy(assetsByCurrencyAtom)[currencyAddress]
      return asset
    })
    return theAtom
  }

  const assetBalance = (currencyAddress: CurrencyAddress) => {
    const theAtom = atom((ctx) => {
      const assetModel = ctx.spy(asset(currencyAddress))
      return assetModel ? ctx.spy(assetModel.balance) : BigNumber(0)
    })
    return theAtom
  }

  const assetBalanceUsd = (currencyAddress: CurrencyAddress) => {
    const theAtom = atom((ctx) => {
      const assetModel = ctx.spy(asset(currencyAddress))
      return assetModel ? ctx.spy(assetModel.balanceUsd) : BigNumber(0)
    })
    return theAtom
  }

  const toDtoAction = action((ctx): WalletApi.WalletDto => {
    const assets = ctx.get(assetsAtom)
    return {
      walletId: dto.walletId,
      name: ctx.get(nameAtom),
      color: ctx.get(colorAtom),
      address: dto.address,
      assets: assets.map((it) => it.toDto(ctx)),
      chain: Network.toDto(network),
      favorite: ctx.get(favoriteAtom),
      delugeId: dto.delugeId,
    }
  })

  return {
    id: WalletId(dto.walletId),
    network,
    name: nameAtom,
    color: colorAtom,
    order: orderAtom,
    address: WalletAddress(dto.address),
    totalBalanceNative,
    totalBalanceUsd,
    nativeAsset: nativeAssetAtom,
    nativeAssetBalance: nativeAssetBalanceAtom,
    nativeAssetBalanceUsd: nativeAssetBalanceUsdAtom,
    favorite: favoriteAtom,
    custody: custodyAtom,
    assets: assetsAtom,
    assetsByCurrency: assetsByCurrencyAtom,
    visibleAssets: visibleAssetsAtom,
    updateFromDto: updateFromDtoAction,
    searchAssets: searchAssetsAction,
    asset,
    assetBalance,
    assetBalanceUsd,
    toDto: toDtoAction,
  }
}
reatomWallet.cmpBalanceAsc = (ctx: Ctx | CtxSpy): Order.Order<WalletModel> => {
  return (a, b) => {
    const balanceA = ctx.spy
      ? ctx.spy(a.totalBalanceNative)
      : ctx.get(a.totalBalanceNative)
    const balanceB = ctx.spy
      ? ctx.spy(b.totalBalanceNative)
      : ctx.get(b.totalBalanceNative)
    return BigNumber_.order(balanceA, balanceB)
  }
}
reatomWallet.cmpBalanceDesc = (ctx: Ctx | CtxSpy): Order.Order<WalletModel> => {
  return Order.flip(reatomWallet.cmpBalanceAsc(ctx))
}
reatomWallet.cmpAssetsCountAsc = (
  ctx: Ctx | CtxSpy,
): Order.Order<WalletModel> => {
  return (a, b) => {
    const assetsCountA = ctx.spy
      ? ctx.spy(a.assets).length
      : ctx.get(a.assets).length
    const assetsCountB = ctx.spy
      ? ctx.spy(b.assets).length
      : ctx.get(b.assets).length
    return Order.Number(assetsCountA, assetsCountB)
  }
}
reatomWallet.cmpAssetsCountDesc = (
  ctx: Ctx | CtxSpy,
): Order.Order<WalletModel> => {
  return Order.flip(reatomWallet.cmpAssetsCountAsc(ctx))
}
reatomWallet.cmpByOrderAsc = (ctx: Ctx | CtxSpy): Order.Order<WalletModel> => {
  return (a, b) => {
    const orderA = ctx.spy ? ctx.spy(a.order) : ctx.get(a.order)
    const orderB = ctx.spy ? ctx.spy(b.order) : ctx.get(b.order)
    return Order.Number(orderA, orderB)
  }
}
reatomWallet.create = action((ctx, dto: WalletApi.WalletDto) => {
  const {
    assets: { tokenLogosHost },
  } = ctx.get(environmentAtom)

  const assetsPairsDtos = pipe(
    dto.assets.map((asset) => asset.pairMeta),
    Array.compact,
  )

  const assetsPairs = Map.create(
    assetsPairsDtos.map(
      (pairDto) =>
        [
          PairAddress(pairDto.poolAddress),
          pairRegistry.resolveFromDto(ctx, pairDto),
        ] as const,
    ),
  )

  return reatomWallet(dto, tokenLogosHost, assetsPairs)
})

export interface WalletModel extends ReturnType<typeof reatomWallet> {}

export const walletDtoUpdateReceivedAction = action<WalletApi.WalletDto>(
  'walletUpdateReceivedAction',
)
